import { useEffect } from 'react';
import { arbitrum } from '@reown/appkit/networks';
import { WagmiAdapter } from '@reown/appkit-adapter-wagmi';
import { createAppKit, useAppKitAccount } from '@reown/appkit/react';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { useReadContract, WagmiProvider } from 'wagmi';
import { getContract } from 'viem';

// Config
import {
  META_MASK_WALLET_ID,
  REOWN_PROJECT_ID,
  TRUST_WALLET_ID,
  isProdEnvironment,
  PRIVY_SALE_ADDRESS,
  abiStorage,
  APP_LINK,
  EXCLUDED_WALLETS,
  PRESALE_VESTING_ADDRESS,
} from '../../config';

// Hooks
import { useGetAdminsList } from '../../hooks/adminHooks/useGetAdminsList';
import { useAppDispatch } from '../../store';

// Actions
import {
  setIsNewContractAdmin,
  setIsOldContractAdmin,
  setIsReferralManager,
  setLockupPeriod,
  setUnlockTime,
  setVestingStartTime,
} from '../../store/slices/mainSlice';

const metadata = {
  name: 'Tribe perpetual',
  description: 'Tribe perpetual',
  url: APP_LINK,
  icons: ['https://assets.reown.com/reown-profile-pic.png'],
};

const arbitrumFixed = {
  ...arbitrum,
  rpcUrls: {
    default: {
      http: ['https://arbitrum-one-rpc.publicnode.com'],
    },
  },
};

export const devnetAppKit = {
  id: 450325,
  caipNetworkId: `eip155:450325`,
  chainNamespace: 'eip155',
  name: 'Devnet',
  nativeCurrency: {
    name: 'ETH',
    symbol: 'ETH',
    decimals: 18,
  },
  rpcUrls: {
    default: {
      http: ['https://eth.dp-dev-site.com'],
    },
    public: {
      http: ['https://eth.dp-dev-site.com'],
    },
  },
  blockExplorers: {
    default: {
      name: 'Explorer',
      url: 'https://eth.dp-dev-site.com',
    },
  },
  contracts: {
    multicall3: {
      address: '0xbFd1bf336a68442dA3e05Ca4d8d9Afd0D53D25e2',
    },
  },
  testnet: true,
};

const networks = isProdEnvironment ? [arbitrumFixed] : [devnetAppKit];

const wagmiAdapter = new WagmiAdapter({
  networks,
  projectId: REOWN_PROJECT_ID,
  ssr: true,
});

export const config = wagmiAdapter.wagmiConfig;

createAppKit({
  adapters: [wagmiAdapter],
  networks,
  metadata,
  projectId: REOWN_PROJECT_ID,
  enableWalletConnect: true,
  enableCoinbase: false,
  allWallets: 'HIDE',
  featuredWalletIds: [TRUST_WALLET_ID, META_MASK_WALLET_ID],
  excludeWalletIds: Object.values(EXCLUDED_WALLETS),
  features: {
    email: false,
    socials: [],
    emailShowWallets: true,
    swaps: false,
    onramp: false,
    send: false,
  },
});

const queryClient = new QueryClient();

const WalletData = () => {
  const oldContract = getContract({
    address: PRIVY_SALE_ADDRESS,
    abi: abiStorage.PrivySale.abi,
  });
  const newContract = getContract({
    address: PRESALE_VESTING_ADDRESS,
    abi: abiStorage.PresaleVesting.abi,
  });

  const dispatch = useAppDispatch();
  const { address } = useAppKitAccount();
  const { adminsList: oldContractAdminsList } = useGetAdminsList({
    defaultContract: { contract: oldContract },
  });
  const { adminsList: newContractAdminsList } = useGetAdminsList({
    defaultContract: { contract: newContract },
  });
  const { adminsList: referralManagersList } = useGetAdminsList({ isReferralManager: true });

  useEffect(() => {
    if (oldContractAdminsList) {
      const isOldContractAdmin = oldContractAdminsList.some(admin => admin.result === address);
      dispatch(setIsOldContractAdmin(isOldContractAdmin));
    }

    if (newContractAdminsList) {
      const isNewContractAdmin = newContractAdminsList.some(admin => admin.result === address);
      dispatch(setIsNewContractAdmin(isNewContractAdmin));
    }

    if (referralManagersList) {
      const isRefManager = referralManagersList.some(admin => admin.result === address);
      dispatch(setIsReferralManager(isRefManager));
    }
  }, [address, oldContractAdminsList, dispatch, referralManagersList, newContractAdminsList]);

  const { data: vestingStartTime } = useReadContract({
    address: PRIVY_SALE_ADDRESS,
    abi: abiStorage.PrivySale.abi,
    functionName: 'vestingStartTime',
  });

  useEffect(() => {
    if (vestingStartTime && !isNaN(parseInt(vestingStartTime))) {
      dispatch(setVestingStartTime(parseInt(vestingStartTime) * 1000));
    }
  }, [vestingStartTime, dispatch]);

  const { data: unlockTime } = useReadContract({
    address: PRESALE_VESTING_ADDRESS,
    abi: abiStorage.PresaleVesting.abi,
    functionName: 'unlockTime',
  });

  useEffect(() => {
    if (unlockTime && !isNaN(parseInt(unlockTime))) {
      dispatch(setUnlockTime(parseInt(unlockTime) * 1000));
    }
  }, [unlockTime, dispatch]);

  const { data: lockupPeriod } = useReadContract({
    address: PRIVY_SALE_ADDRESS,
    abi: abiStorage.PrivySale.abi,
    functionName: 'LOCKUP_PERIOD',
  });

  useEffect(() => {
    if (lockupPeriod) {
      dispatch(setLockupPeriod(parseInt(lockupPeriod) * 1000));
    }
  }, [lockupPeriod, dispatch]);

  return null;
};

const AppKitProvider = ({ children }) => {
  return (
    <WagmiProvider config={config}>
      <QueryClientProvider client={queryClient}>
        <WalletData />
        {children}
      </QueryClientProvider>
    </WagmiProvider>
  );
};

export default AppKitProvider;
